import { useState, useCallback } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, query, orderBy, getDocs, where, serverTimestamp } from "firebase/firestore";
import { storage, db } from "../firebase";
import { logAudit } from "../utils/auditTrail";

function validateImageMagicBytes(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      // JPEG: FF D8 FF
      if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) return resolve(true);
      // PNG: 89 50 4E 47
      if (arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) return resolve(true);
      // WebP: 52 49 46 46 ... 57 45 42 50
      if (arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46 && arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50) return resolve(true);
      resolve(false);
    };
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
}

const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.8;

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error("Compression failed")),
        "image/jpeg",
        JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
    img.src = url;
  });
}

export default function usePhotoUpload(patientId) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const loadPhotos = useCallback(async (regionFilter = null) => {
    if (!patientId) return;
    setLoading(true);
    try {
      let q;
      if (regionFilter) {
        q = query(
          collection(db, "patients", patientId, "photos"),
          where("regionCorporal", "==", regionFilter),
          orderBy("createdAt", "desc")
        );
      } else {
        q = query(
          collection(db, "patients", patientId, "photos"),
          orderBy("createdAt", "desc")
        );
      }
      const snap = await getDocs(q);
      setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading photos:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const uploadPhoto = useCallback(async (file, { regionCorporal, descripcion, recordId, user }) => {
    if (!patientId) throw new Error("No patientId");
    setUploading(true);
    setUploadProgress("Comprimiendo...");
    try {
      const isValidImage = await validateImageMagicBytes(file);
      if (!isValidImage) throw new Error("Archivo no es una imagen valida (JPEG/PNG/WebP)");
      if (file.size > 10 * 1024 * 1024) throw new Error("Archivo excede 10MB");
      const compressed = await compressImage(file);
      const ts = Date.now();
      const regionSlug = regionCorporal.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
      const storagePath = `photos/${patientId}/${recordId || "general"}/${ts}_${regionSlug}.jpg`;
      const storageRef = ref(storage, storagePath);

      setUploadProgress("Subiendo...");
      await uploadBytes(storageRef, compressed, { contentType: "image/jpeg" });
      const url = await getDownloadURL(storageRef);

      setUploadProgress("Guardando registro...");
      const photoDoc = {
        url,
        storagePath,
        regionCorporal,
        descripcion: descripcion || "",
        recordId: recordId || null,
        doctorId: user.uid,
        doctorNombre: user.displayName || user.email,
        sizeBytes: compressed.size,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "patients", patientId, "photos"), photoDoc);
      const newPhoto = { id: docRef.id, ...photoDoc, createdAt: new Date() };
      setPhotos(prev => [newPhoto, ...prev]);
      await logAudit("subir_foto", "photo", docRef.id, user.uid, user.email || "", `Paciente: ${patientId}, Región: ${regionCorporal}`);
      return newPhoto;
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [patientId]);

  return { photos, loading, uploading, uploadProgress, loadPhotos, uploadPhoto };
}
